import { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';

import { useUpdateAccessRightsModulesMutation } from '@app/api/AccessRights';
import {
  AI_OPERATOR_LABEL,
  ERROR,
  ORGANIZATIONS_PATH,
  SUCCESS,
} from '@app/constants';
import { useUserPrint } from '@app/hooks';
import { NotificationContext } from '@app/providers';
import { handleCatchError } from '@app/services';
import {
  useGetModulesRightsByIdQuery,
  useLazyGetAiQuery,
} from '@app/services/rtkQuery/organization';
import { useErrorHandler } from '@app/shared/hooks';
import type { NAccessRights } from '@app/types';
import type { NOrganization } from '@app/types/Organization';

const RIGHTS_TO_AI_ID = 8;

export const useModuleAccessRightsByOrganization = () => {
  const { activeBranchId } = useUserPrint();

  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { showNotification } = useContext(NotificationContext);

  const organizationId = Number(searchParams.get('id')) || undefined;

  const {
    data,
    isLoading,
    refetch: refetchModulesWithRights,
    error,
    isError,
  } = useGetModulesRightsByIdQuery(
    {
      id: Number(organizationId),
      ActiveBranchId: activeBranchId,
    },
    {
      skip: !organizationId,
    }
  );

  useErrorHandler([{ flag: isError, error }]);

  const [initialRights, setInitialRights] = useState<
    NAccessRights.AccessRightsModuleDto[]
  >(data?.Modules || []);
  const [rights, changeRights] = useState<
    NAccessRights.AccessRightsModuleDto[]
  >(data?.Modules || []);
  const [info, changeInfo] = useState<NOrganization.OrganizationInfo>();

  const [updateOrganizationAccessRights, { isLoading: isSaving }] =
    useUpdateAccessRightsModulesMutation();

  const [getModulesWithAccessToAi] = useLazyGetAiQuery();

  const saveRights = useCallback(async () => {
    if (rights?.length > 0 && organizationId) {
      const payload = rights.map(({ Name, RightType, Allowed }) => ({
        Name,
        RightType,
        Allowed,
      }));

      try {
        await updateOrganizationAccessRights({
          organizationId,
          ActiveBranchId: activeBranchId,
          data: payload,
        }).unwrap();

        setInitialRights(rights);

        refetchModulesWithRights();

        const initialAccessToAi = initialRights.find(
          ({ RightType }) => RightType === RIGHTS_TO_AI_ID
        )?.Allowed;
        const currentAccessToAi = rights.find(
          ({ RightType }) => RightType === RIGHTS_TO_AI_ID
        )?.Allowed;
        const isAccessToAiChanged = initialAccessToAi !== currentAccessToAi;

        if (isAccessToAiChanged) {
          getModulesWithAccessToAi({ ActiveBranchId: activeBranchId });
        }

        showNotification('', SUCCESS);
      } catch (error) {
        showNotification(handleCatchError(error), ERROR);
      }
    }
  }, [
    rights,
    organizationId,
    updateOrganizationAccessRights,
    refetchModulesWithRights,
    initialRights,
    getModulesWithAccessToAi,
    activeBranchId,
    showNotification,
  ]);

  useEffect(() => {
    if (!organizationId) {
      navigate(ORGANIZATIONS_PATH);
    }
  }, [organizationId, navigate]);

  useEffect(() => {
    if (data) {
      const { Modules, ...info } = data;

      const renamedModules = Modules.map((item) => {
        const { Name, RightType } = item;

        if (RightType === RIGHTS_TO_AI_ID) {
          return {
            ...item,
            Name: AI_OPERATOR_LABEL,
          };
        }

        if (Name === 'Whats App интеграции') {
          return {
            ...item,
            Name: 'WhatsApp-интеграции',
          };
        }

        return item;
      });

      changeRights(renamedModules);
      setInitialRights(renamedModules);
      changeInfo({
        Id: info.Id.toString(),
        Name: info.Name || '',
      });
    }
  }, [data]);

  const hasAnyChanges = useMemo(() => {
    const serializedInitialRights = JSON.stringify(initialRights);
    const serializedRights = JSON.stringify(rights);
    const hasChanges = serializedInitialRights !== serializedRights;

    return hasChanges;
  }, [initialRights, rights]);

  return {
    hasAnyChanges,
    info,
    rights,
    isLoading,
    isSaving,
    saveRights,
    changeRights,
    isError,
  };
};
